// Configuración → Bot de WhatsApp (§1.3): estado, consultas de hoy, última respuesta, el
// mensaje del bot, los últimos mensajes y, plegado, «Conexión con Meta y pruebas».
// El modal de credenciales precarga lo guardado y manda solo lo que cambió (P-21).
import React, { useEffect, useState } from "react";
import { Check, ChevronDown, Copy, FlaskConical, KeyRound, MessageCircle, Smartphone, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import type { DiagResponse, WhatsAppLog } from "../types";
import { adminFetch } from "../api";
import { BotMessageEditor } from "../components/BotMessageEditor";
import { WhatsAppActivation } from "../components/WhatsAppActivation";
import { Alert, Badge, Button, Card, Empty, Field, ICON, IconButton, Input, Modal, Select, Skeleton, Toggle, useToast } from "./ui";
import { ConfigPage, type CfgView } from "./ConfigPage";

const TZ = "America/Argentina/Buenos_Aires";
const HIDE_KEY = "tandil_hide_bot_logs";
const SIM_WORDS = ["hola", "hoy", "mañana", "soporte"];
const FORMATS = [
  { value: "strip9", label: "Quitar el 9 (producción): 542494694726" },
  { value: "keep9", label: "Mantener el 9 (sandbox internacional): 5492494694726" },
  { value: "add15", label: "Insertar 15 (sandbox nacional, error 131030): 54249154694726" },
];

const SECTION = "flex flex-col gap-[18px] px-4 py-[18px] md:p-6";
const H3 = "text-[15px] font-semibold text-adm-text";
const SUB = "text-[13px] leading-[1.55] text-adm-t55";
const STAT = "flex min-w-0 flex-col gap-2 px-4 py-3.5 md:px-5 md:py-[18px]";
const LBL = "text-xs font-medium text-adm-t55";

function readHide(): boolean {
  try {
    return localStorage.getItem(HIDE_KEY) !== "0";
  } catch {
    return true;
  }
}
function writeHide(value: boolean) {
  try {
    localStorage.setItem(HIDE_KEY, value ? "1" : "0");
  } catch {
    // sin almacenamiento: no se recuerda, nada más
  }
}

/** /diag devuelve «Faltante (No configurado)» cuando no hay dato */
const missing = (v?: string) => !v || v.startsWith("Faltante");
const inAR = (iso: string) => DateTime.fromISO(iso).setZone(TZ);
const isToday = (iso: string) => inAR(iso).hasSame(DateTime.now().setZone(TZ), "day");

function ago(iso?: string): string {
  if (!iso) return "Sin respuestas";
  const minutes = Math.round(DateTime.now().diff(DateTime.fromISO(iso), "minutes").minutes);
  if (minutes < 1) return "recién";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return inAR(iso).setLocale("es").toFormat("d LLL");
}

function logBadge(log: WhatsAppLog) {
  if (log.status === "error") return <Badge tone="red">Error de Meta</Badge>;
  if (log.status === "simulated") return <Badge tone="neutral">Simulado</Badge>;
  if (log.direction === "outbound") return <Badge tone="ok">Respondido</Badge>;
  return <Badge tone="neutral">Recibido</Badge>;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyField({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded-[10px] border border-adm-border bg-adm-card px-3 py-2.5 font-mono text-xs text-adm-text">{value}</code>
        <IconButton
          label={copied ? "Copiado" : `Copiar: ${label}`}
          onClick={async () => {
            if (await copyText(value)) {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            } else {
              toast.show("No se pudo copiar. Seleccionalo y copialo a mano.", "error");
            }
          }}
        >
          {copied ? <Check {...ICON} /> : <Copy {...ICON} />}
        </IconButton>
      </div>
    </Field>
  );
}

type CredView = { phoneId: string; verifyToken: string; hasAccessToken: boolean; accessTokenMasked: string; argentinaFormat: string };

/** P-21: precarga lo guardado; el token nunca vuelve completo (campo vacío = no cambiar). */
function CredentialsModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const toast = useToast();
  const [saved, setSaved] = useState<CredView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState({ phoneId: "", verifyToken: "", accessToken: "", argentinaFormat: "keep9" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = async () => {
    setSaved(null);
    setLoadError(null);
    setSaveError(null);
    try {
      const res = await adminFetch("/api/whatsapp/config");
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      setSaved(data);
      setForm({ phoneId: data.phoneId || "", verifyToken: data.verifyToken || "", accessToken: "", argentinaFormat: data.argentinaFormat || "keep9" });
    } catch (err: any) {
      setLoadError(err?.message || "No se pudo leer lo guardado.");
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  // Solo lo que cambió y no está vacío
  const patch: Record<string, string> = {};
  if (saved) {
    const phoneId = form.phoneId.trim();
    const verifyToken = form.verifyToken.trim();
    const accessToken = form.accessToken.trim();
    if (phoneId && phoneId !== saved.phoneId) patch.phoneId = phoneId;
    if (verifyToken && verifyToken !== saved.verifyToken) patch.verifyToken = verifyToken;
    if (accessToken) patch.accessToken = accessToken;
    if (form.argentinaFormat !== saved.argentinaFormat) patch.argentinaFormat = form.argentinaFormat;
  }
  const changes = Object.keys(patch).length;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!saved || changes === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await adminFetch("/api/whatsapp/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      toast.show("Credenciales guardadas");
      onClose();
      await onSaved();
    } catch (err: any) {
      setSaveError(err?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      eyebrow="Bot de WhatsApp"
      title="Credenciales de Meta"
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="cred-form" loading={saving} disabled={!saved || changes === 0}>
            {changes === 0 && saved ? "Sin cambios" : "Guardar"}
          </Button>
        </>
      }
    >
      {!saved && !loadError && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-[66px]" />
          <Skeleton className="h-[66px]" />
          <Skeleton className="h-[66px]" />
          <Skeleton className="h-[66px]" />
        </div>
      )}
      {loadError && (
        <Alert tone="red" action={<Button size="sm" onClick={load}>Reintentar</Button>}>
          No se pudo leer lo guardado: {loadError} Hasta que cargue no se puede guardar, para no pisar las credenciales.
        </Alert>
      )}
      {saved && (
        <form id="cred-form" onSubmit={save} className="flex flex-col gap-4" autoComplete="off">
          <Field label="ID de teléfono" htmlFor="cred-phone" hint="Meta → WhatsApp → Configuración de la API → «Phone number ID». Vacío = queda el guardado.">
            <Input id="cred-phone" mono autoComplete="off" placeholder="104845550000000" value={form.phoneId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, phoneId: e.target.value })} />
          </Field>
          <Field label="Token de verificación del webhook" htmlFor="cred-verify" hint="El mismo que pusiste en Meta → Webhooks.">
            <Input id="cred-verify" mono autoComplete="off" value={form.verifyToken} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, verifyToken: e.target.value })} />
          </Field>
          <Field
            label="Token de acceso"
            htmlFor="cred-token"
            hint={saved.hasAccessToken ? `Guardado: ${saved.accessTokenMasked}. Dejalo vacío para no cambiarlo.` : "Todavía no hay uno guardado."}
          >
            <Input
              id="cred-token"
              name="meta-access-token"
              type="password"
              mono
              autoComplete="new-password"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              placeholder={saved.hasAccessToken ? "Pegá uno nuevo solo para reemplazarlo" : "Pegá el token de Meta"}
              value={form.accessToken}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, accessToken: e.target.value })}
            />
          </Field>
          <Field label="Formato de los números de Argentina" htmlFor="cred-format" hint="Si Meta da el error 131030 en el sandbox, probá «Insertar 15».">
            <Select id="cred-format" value={form.argentinaFormat} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setForm({ ...form, argentinaFormat: e.target.value })}>
              {FORMATS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
          <details className="text-[13px] text-adm-t55">
            <summary className="cursor-pointer font-medium text-adm-t70">¿De dónde salen estos datos?</summary>
            <p className="pt-2 leading-[1.55]">
              En developers.facebook.com, en la app, producto WhatsApp: «Configuración de la API» tiene el ID de teléfono y el token de acceso;
              «Configuración» → Webhooks tiene la URL del webhook y el token de verificación.
            </p>
          </details>
          {saveError && <Alert tone="red">No se guardó: {saveError}</Alert>}
        </form>
      )}
    </Modal>
  );
}

function SimulateModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => Promise<unknown> }) {
  const [phone, setPhone] = useState("5492494123456");
  const [text, setText] = useState("hola");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  const run = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !text.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await adminFetch("/api/whatsapp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entry: [{ changes: [{ value: { messages: [{ from: phone.trim(), type: "text", text: { body: text.trim() } }] } }] }],
        }),
      });
      if (!res.ok) throw new Error(`El servidor respondió ${res.status}.`);
      setResult({ ok: true, text: "Listo: el servidor procesó el mensaje. La respuesta aparece en «Últimos mensajes»." });
      await onDone();
    } catch (err: any) {
      setResult({ ok: false, text: err?.message || "Sin conexión con el servidor." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Bot de WhatsApp"
      title="Simular un mensaje"
      footer={
        <>
          <Button onClick={onClose}>Cerrar</Button>
          <Button variant="primary" type="submit" form="sim-form" loading={busy} icon={<FlaskConical {...ICON} />}>
            Simular
          </Button>
        </>
      }
    >
      <form id="sim-form" onSubmit={run} className="flex flex-col gap-4">
        <p className={SUB}>Hace como si alguien le escribiera al bot, para ver qué contesta.</p>
        <Field label="Número" htmlFor="sim-phone">
          <Input id="sim-phone" mono inputMode="tel" value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)} />
        </Field>
        <Field label="Mensaje" htmlFor="sim-text">
          <div className="flex flex-wrap gap-1.5">
            {SIM_WORDS.map((w) => (
              <Button key={w} size="sm" variant={text === w ? "primary" : "secondary"} onClick={() => setText(w)}>
                {w}
              </Button>
            ))}
          </div>
          <Input id="sim-text" placeholder="¿Quién está de turno hoy?" value={text} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)} />
        </Field>
        {result &&
          (result.ok ? (
            <p className="flex items-center gap-1.5 text-sm text-adm-acento-texto">
              <Check {...ICON} />
              {result.text}
            </p>
          ) : (
            <Alert tone="red">No se pudo simular: {result.text}</Alert>
          ))}
      </form>
    </Modal>
  );
}

export function BotPage({
  diag,
  diagError,
  onReload,
  onNavigate,
}: {
  diag: DiagResponse | null;
  diagError: string | null;
  onReload: () => Promise<unknown>; // fetchData devuelve boolean desde la Ficha 4
  onNavigate: (view: CfgView) => void;
}) {
  const toast = useToast();
  const [hideBot, setHideBot] = useState<boolean>(readHide);
  const [editing, setEditing] = useState(false);
  const [modal, setModal] = useState<null | "cred" | "sim" | "act">(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    writeHide(hideBot);
  }, [hideBot]);

  useEffect(() => {
    if (!confirmClear) return;
    const t = window.setTimeout(() => setConfirmClear(false), 4000);
    return () => window.clearTimeout(t);
  }, [confirmClear]);

  const logs = diag?.logs || [];
  const visible = logs.filter((l) => !hideBot || l.direction !== "outbound");
  const inboundToday = logs.filter((l) => l.direction === "inbound" && isToday(l.timestamp)).length;
  // /diag manda hasta 50: si el más viejo también es de hoy, puede haber más
  const truncated = logs.length >= 50 && isToday(logs[logs.length - 1].timestamp);
  const lastReply = logs.find((l) => l.direction === "outbound");
  const noCreds = !!diag && (missing(diag.config?.WHATSAPP_PHONE_NUMBER_ID) || missing(diag.config?.WHATSAPP_ACCESS_TOKEN_MASKED));
  const webhookUrl = diag?.webhookUrl || `${window.location.origin}/api/whatsapp`;

  const clearLogs = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setConfirmClear(false);
    setClearing(true);
    try {
      const res = await adminFetch("/api/whatsapp/logs/clear", { method: "POST" });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      }
      await onReload();
      toast.show("Historial vaciado");
    } catch (err: any) {
      toast.show(`No se vació el historial. ${err?.message || ""}`.trim(), "error");
    } finally {
      setClearing(false);
    }
  };

  let body: React.ReactNode;
  if (!diag && !diagError) {
    body = (
      <div className="flex flex-col gap-3" aria-busy="true">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Skeleton className="col-span-2 h-[104px] rounded-[18px] md:col-span-1" />
          <Skeleton className="h-[104px] rounded-[18px]" />
          <Skeleton className="h-[104px] rounded-[18px]" />
        </div>
        <Skeleton className="h-[220px] rounded-[18px]" />
      </div>
    );
  } else if (!diag) {
    body = (
      <Alert tone="red" action={<Button size="sm" onClick={() => onReload()}>Reintentar</Button>}>
        No se pudo leer el estado del bot. {diagError}
      </Alert>
    );
  } else {
    body = (
      <>
        {diagError && <Alert tone="amber">No se pudo actualizar: {diagError} Lo que ves es lo último que llegó.</Alert>}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Card className={`${STAT} col-span-2 md:col-span-1`}>
            <span className={LBL}>Estado</span>
            <span>
              {noCreds ? (
                <Badge tone="red">Sin credenciales</Badge>
              ) : lastReply?.status === "error" ? (
                <Badge tone="amber">La última respuesta falló</Badge>
              ) : (
                <Badge tone="ok" icon={Check}>
                  Conectado
                </Badge>
              )}
            </span>
            <span className={`${SUB} truncate`}>ID de teléfono {diag.config?.WHATSAPP_PHONE_NUMBER_ID}</span>
          </Card>
          <Card className={STAT}>
            <span className={LBL}>Consultas hoy</span>
            <span className="text-2xl font-medium leading-none tracking-[-0.03em] md:text-[30px]">
              {inboundToday}
              {truncated ? "+" : ""}
            </span>
          </Card>
          <Card className={STAT}>
            <span className={LBL}>Última respuesta</span>
            <span className="text-[22px] font-medium leading-none tracking-[-0.03em]" title={lastReply ? inAR(lastReply.timestamp).toFormat("dd/MM HH:mm") : undefined}>
              {ago(lastReply?.timestamp)}
            </span>
          </Card>
        </div>

        <Card className={SECTION}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <h3 className={H3}>Lo que contesta el bot</h3>
              <p className={SUB}>Íconos, negrita, cursiva y la frase final del mensaje de turnos.</p>
            </div>
            <Button size="sm" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>
              {editing ? "Cerrar" : "Editar"}
            </Button>
          </div>
          {editing && <BotMessageEditor />}
        </Card>

        <div className="flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className={H3}>Últimos mensajes</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Toggle checked={hideBot} onChange={setHideBot} label="Ocultar respuestas del bot" />
              {logs.length > 0 && (
                <Button
                  size="sm"
                  variant={confirmClear ? "danger" : "ghost"}
                  tone={confirmClear ? undefined : "danger"}
                  icon={<Trash2 {...ICON} />}
                  loading={clearing}
                  onClick={clearLogs}
                >
                  {confirmClear ? "Tocá de nuevo para vaciar" : "Vaciar historial"}
                </Button>
              )}
            </div>
          </div>
          {visible.length === 0 ? (
            <Empty size="sm" icon={MessageCircle} title={logs.length ? "Solo hay respuestas del bot" : "Sin mensajes todavía"}>
              {logs.length ? "Apagá «Ocultar respuestas del bot» para verlas." : "Cuando alguien le escriba al bot, aparece acá."}
            </Empty>
          ) : (
            <div className="overflow-hidden rounded-[14px] border border-adm-border bg-adm-surface">
              {visible.map((log, i) => (
                <div
                  key={log.id || `${log.timestamp}-${i}`}
                  className="grid grid-cols-[48px_minmax(0,1fr)] items-center gap-3.5 border-b border-adm-border px-4 py-3 text-sm last:border-b-0 md:grid-cols-[56px_120px_minmax(0,1fr)_auto]"
                >
                  <span className="font-mono text-xs text-adm-t55" title={inAR(log.timestamp).toFormat("dd/MM/yyyy HH:mm:ss")}>
                    {isToday(log.timestamp) ? inAR(log.timestamp).toFormat("HH:mm") : inAR(log.timestamp).toFormat("dd/MM")}
                  </span>
                  <span className="truncate font-mono text-xs text-adm-t55 max-md:hidden">{log.direction === "outbound" ? log.to : log.from}</span>
                  <span className="min-w-0">
                    <span className="block truncate" title={log.body}>
                      {log.body}
                    </span>
                    {log.status === "error" && log.error && (
                      <span className="block truncate text-xs text-adm-rojo" title={log.error}>
                        {log.error}
                      </span>
                    )}
                  </span>
                  <span className="max-md:hidden">{logBadge(log)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <Card className="px-4 py-[18px] md:p-6">
          <details className="group">
            <summary className="flex min-h-9 cursor-pointer list-none items-center justify-between text-sm font-medium text-adm-t70 [&::-webkit-details-marker]:hidden">
              Conexión con Meta y pruebas
              <ChevronDown {...ICON} className="transition-transform group-open:rotate-180" aria-hidden="true" />
            </summary>
            <div className="flex flex-col gap-4 pt-3.5">
              <CopyField label="URL del webhook" value={webhookUrl} />
              <CopyField label="Token de verificación" value={diag.config?.WHATSAPP_VERIFY_TOKEN || ""} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" icon={<KeyRound {...ICON} />} onClick={() => setModal("cred")}>
                  Credenciales de Meta
                </Button>
                <Button size="sm" icon={<FlaskConical {...ICON} />} onClick={() => setModal("sim")}>
                  Simular un mensaje
                </Button>
                <Button size="sm" icon={<Smartphone {...ICON} />} onClick={() => setModal("act")}>
                  Activar número
                </Button>
              </div>
            </div>
          </details>
        </Card>
      </>
    );
  }

  return (
    <ConfigPage view="cfg:bot" role="super" onNavigate={onNavigate}>
      {body}
      <CredentialsModal open={modal === "cred"} onClose={() => setModal(null)} onSaved={onReload} />
      <SimulateModal open={modal === "sim"} onClose={() => setModal(null)} onDone={onReload} />
      <Modal open={modal === "act"} onClose={() => setModal(null)} eyebrow="Meta Cloud API" title="Activar número">
        <WhatsAppActivation />
      </Modal>
    </ConfigPage>
  );
}
